import React from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Typography,
  Paper
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  Visibility,
  Print,
  Business,
  ShoppingCart,
  Edit,
  TaskAlt,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
  getRequest,
  postRequest,
  patchRequest
} from "Includes/api/apicall";
import { GET_URL, PATCH_URL, POST_URL } from "Includes/urls";
import { erpAmountBeforeGst, idCardAmountTotal, parseBillingDateOnly } from "./agreementFinance";

const STATUS_LABELS = {
  DRAFT: "Draft",
  SIGNED: "Signed",
  SIGNED_AND_CREATED: "Company Created",
  CREATED: "Ordered",
  CANCELLED: "Cancelled",
};
const ACTIVE_AGREEMENT_STATUSES = ["DRAFT", "SIGNED", "SIGNED_AND_CREATED", "CREATED"];
const AGREEMENT_PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_AGREEMENT_PAGE_SIZE = 10;

function AgreementListPage(props) {
  const navigate = useNavigate();
  // 🔴 ORIGINAL STATE (UNCHANGED)
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_AGREEMENT_PAGE_SIZE);
  const [billingDialogOpen, setBillingDialogOpen] = React.useState(false);
  const [billingAgreementRow, setBillingAgreementRow] = React.useState(null);
  const [billingYears, setBillingYears] = React.useState([]);
  const [billingUsers, setBillingUsers] = React.useState([]);
  const [billingForm, setBillingForm] = React.useState({
    year: "",
    student_strength: "",
    coordinator: "",
  });
  const [billingSubmitting, setBillingSubmitting] = React.useState(false);

  const [filters, setFilters] = React.useState({
    sales_person: "",
    agreementDateFrom: "",
    agreementDateTo: "",
    search: "",
    status: ACTIVE_AGREEMENT_STATUSES,
  });

  const [salesPeopleOptions, setSalesPeopleOptions] = React.useState([]);

  React.useEffect(() => {
    getRequest(GET_URL.user.api, { groups: 3 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const users = response.data?.data || response.data || [];
          setSalesPeopleOptions(users);
        }
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    getRequest(GET_URL.academicyear.api, {}, props)
      .then((res) => {
        if (res?.status === 200) {
          setBillingYears(res.data?.data || res.data || []);
        }
      })
      .catch(() => {});
    getRequest(GET_URL.user.api, { groups: 4 }, props)
      .then((res) => {
        if (res?.status === 200) {
          setBillingUsers(res.data?.data || res.data || []);
        }
      })
      .catch(() => {});
  }, []);

  const buildParams = (f) => {
    const params = {
      limit: pageSize,
      pageno: page + 1,
      page: page + 1,
      page_size: pageSize,
    };
    if (f?.sales_person) params.sales_person = f.sales_person;
    if (f?.agreementDateFrom) params.agreement_date_from = f.agreementDateFrom;
    if (f?.agreementDateTo) params.agreement_date_to = f.agreementDateTo;
    if (f?.search) params.search = f.search;
    if (f?.status && f.status.length > 0) {
      params.status = f.status.join(",");
    }
    return params;
  };

  const loadRows = React.useCallback((f) => {
    setLoading(true);
    getRequest(GET_URL.agreements.api, buildParams(f), props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
          setRows(data);
          const totalCount =
            response.data?.count ??
            response.data?.total_count ??
            response.data?.total ??
            data.length;
          setRowCount(totalCount);
        }
      })
      .finally(() => setLoading(false));
  }, [props, page, pageSize]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      loadRows(filters);
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  React.useEffect(() => {
    setPage(0);
  }, [filters.sales_person, filters.agreementDateFrom, filters.agreementDateTo, filters.search, filters.status]);

  const handlePreview = (row) => {
    navigate("/agreement/preview", { state: { data: row } });
  };

  const handlePrint = (row) => {
    navigate("/agreement/preview", { state: { data: row, autoPrint: true } });
  };

  const handleEdit = (row) => {
    navigate(`/agreement/edit?id=${row.id}`);
  };

  const handleMakeSigned = (row) => {
    if (!row?.id || row.status !== "DRAFT") return;
    patchRequest(`${PATCH_URL.agreements.api}${row.id}/`, { status: "SIGNED" }, props)
      .then((res) => {
        if (res?.status === 200) {
          Swal.fire("Success", "Agreement marked as signed", "success");
          loadRows(filters);
        }
      })
      .catch(() => {
        Swal.fire("Error", "Could not update status", "error");
      });
  };

  const getStatusColor = (status) => {
    if (status === "SIGNED") return "success";
    if (status === "SIGNED_AND_CREATED") return "info";
    if (status === "CREATED") return "primary";
    if (status === "CANCELLED") return "error";
    return "default";
  };

  const mapAgreementToCompanyPrefill = (row) => {
    const instituteName = String(row?.schoolName || "").trim();
    const instituteCodeSeed = instituteName || `AGR${row?.id || ""}`;
    const safeCode = instituteCodeSeed
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 20);
    const dbKey = safeCode || `AGR_${row?.id || "NEW"}`;
    return {
      agreementId: row?.id,
      agreementData: row,
      name: instituteName,
      code: dbKey,
      database_key: dbKey.toLowerCase(),
      database_name: dbKey.toLowerCase(),
      domain: `${dbKey.toLowerCase()}.edubricz.local`,
      enquiry_format: `${dbKey}/ENQ`,
      application_format: `${dbKey}/APP`,
      admission_format: `${dbKey}/ADM`,
      from_date: row?.fromDate || "",
      to_date: row?.toDate || "",
      contact_person1: row?.contactPerson || "",
      contact_person2: row?.contactPerson || "",
      tel_num: row?.contactPersonPhoneno || "",
      tel_num_2: row?.contactPersonPhoneno || "",
      address: row?.schoolAddress || "",
      secondary_billing_address: row?.schoolAddress || "",
      secondary_map_location: "",
      secondary_client_billing_status: "GOOD",
      gstin_num: "",
      board_name: "",
      type: "",
      country: "",
      state: "",
      district: "",
      city: "",
      pincode: "",
      total_aount: "",
      paid_aount: "",
    };
  };

  const handleCreateCompany = (row) => {
    navigate("/company/add", {
      state: {
        agreementPrefill: mapAgreementToCompanyPrefill(row),
      },
    });
  };

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const getInclusiveMonths = (fromDate, toDate) => {
    const from = fromDate ? parseBillingDateOnly(fromDate) : null;
    const to = toDate ? parseBillingDateOnly(toDate) : null;
    if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 1;
    const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
    return months > 0 ? months : 1;
  };

  const getInclusiveYears = (fromDate, toDate) => {
    const from = fromDate ? parseBillingDateOnly(fromDate) : null;
    const to = toDate ? parseBillingDateOnly(toDate) : null;
    if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 1;
    let years = to.getFullYear() - from.getFullYear();
    if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
      years += 1;
    }
    return years > 0 ? years : 1;
  };

  const buildAgreementPaymentTermsForOrders = (agreementRow, fallbackDate) => {
    const pts = Array.isArray(agreementRow?.paymentTerms) ? agreementRow.paymentTerms : [];
    const orderDateStr = fallbackDate || new Date().toISOString().slice(0, 10);
    const mapped = pts.map((t, i) => {
      const pct = parseFloat(t?.payment);
      const raw = t?.date || orderDateStr;
      const termDateStr =
        String(raw) < String(orderDateStr) ? orderDateStr : raw;
      return {
        term_name: `Term ${i + 1}`,
        term_receive_date: termDateStr,
        amount_percentage: Number.isFinite(pct) ? pct : null,
        amount_rs: null,
      };
    });
    if (mapped.length > 0) {
      return mapped;
    }
    return [
      {
        term_name: "Term 1",
        term_receive_date: fallbackDate,
        amount_percentage: 100,
        amount_rs: null,
      },
    ];
  };

  const buildPricingServicesFromAgreement = async (agreementRow, studentStrengthOverride = null) => {
    const [plansRes, digitalRes] = await Promise.all([
      getRequest(GET_URL.plans.api, {}, props),
      getRequest(GET_URL.digitalmarketingfeature.api, {}, props),
    ]);
    const plans = plansRes?.data?.data || plansRes?.data || [];
    const digitalFeatureList = digitalRes?.data?.data || digitalRes?.data || [];
    const digitalById = new Map(digitalFeatureList.map((x) => [String(x.id), x]));

    const digitalRaw =
      agreementRow?.digitalfeature && typeof agreementRow.digitalfeature === "object"
        ? agreementRow.digitalfeature
        : {};
    let agreementHasBillableDigitalFeatures = false;
    for (const [, meta] of Object.entries(digitalRaw)) {
      let amt = 0;
      if (Array.isArray(meta)) amt = toNum(meta[0]);
      else if (meta && typeof meta === "object") amt = toNum(meta.price);
      if (round2(amt) > 0) {
        agreementHasBillableDigitalFeatures = true;
        break;
      }
    }

    /**
     * Catalog plan meant for agreement "other services" (digital) rows — not the core ERP licence plan.
     * Without this, digitalPlan fell back to plans[0]; if that was the same as erpPlan (e.g. "New ERP"),
     * a duplicate ServiceOrder appeared on the ERP plan.
     */
    const catalogPlanIsDigitalCarrier = (p) => {
      if (!p) return false;
      const dm = p.digital_marketing_features;
      if (Array.isArray(dm) && dm.length > 0) return true;
      const st = String(p.service_type || "").toLowerCase();
      if (st === "digital_marketing") return true;
      const svc = String(p.service_name || "").toLowerCase();
      const nm = String(p.name || "").toLowerCase();
      if (svc.includes("digital marketing")) return true;
      if ((svc.includes("digital") || nm.includes("digital")) && !svc.includes("erp")) return true;
      return false;
    };

    const erpSvc = (p) => String(p?.service_name || "").trim().toLowerCase();
    const erpNm = (p) => String(p?.name || "").trim().toLowerCase();
    const erpCandidatesFiltered = plans.filter(
      (p) => !catalogPlanIsDigitalCarrier(p) && erpSvc(p).includes("erp"),
    );
    const scoreErpPlan = (p) => {
      const s = erpSvc(p);
      const n = erpNm(p);
      let score = 0;
      if (s === "erp") score += 200;
      if (n === "erp") score += 80;
      if (s.endsWith(" erp")) score += 20;
      if (/\bnew\s*erp\b|^newerp$/i.test(s) || /\bnew\s*erp\b|^newerp$/i.test(n)) score -= 100;
      if (/\bstudio\b|\bmarketing\b|\bdigital\b/.test(`${s} ${n}`)) score -= 50;
      return score - Number(p.id || 0) / 1000000000;
    };
    const erpPlan =
      (erpCandidatesFiltered.length
        ? [...erpCandidatesFiltered].sort((a, b) => scoreErpPlan(b) - scoreErpPlan(a))[0]
        : null) ||
      plans.find((p) => !catalogPlanIsDigitalCarrier(p)) ||
      plans[0];

    const idCardPlan =
      plans.find((p) => {
        const serviceName = String(p?.service_name || "").toLowerCase();
        const planName = String(p?.name || "").toLowerCase();
        const blob = `${serviceName} ${planName}`;
        return (
          blob.includes("id card") ||
          blob.includes("idcard") ||
          blob.includes("identity card")
        );
      }) || erpPlan || plans[0];

    let digitalPlan =
      plans.find((p) => catalogPlanIsDigitalCarrier(p)) ||
      plans.find(
        (p) =>
          String(p?.service_name || "").toLowerCase().includes("digital") &&
          String(p?.id) !== String(erpPlan?.id),
      );

    if (digitalPlan && erpPlan && String(digitalPlan.id) === String(erpPlan.id)) {
      digitalPlan =
        plans.find(
          (p) =>
            String(p.id) !== String(erpPlan.id) &&
            (catalogPlanIsDigitalCarrier(p) ||
              String(p?.service_name || "").toLowerCase().includes("digital")),
        ) || null;
    }

    if (!erpPlan || !idCardPlan) {
      throw new Error("No service plan found. Please create ERP and ID Card plans in the catalog.");
    }
    if (agreementHasBillableDigitalFeatures && !digitalPlan) {
      throw new Error(
        "Agreement includes other services but no Digital Marketing plan was found in the catalog. Add a plan with digital marketing modules (JSON on the plan) or a separate digital service.",
      );
    }

    const agreementDate = agreementRow?.agreementdate || new Date().toISOString().slice(0, 10);
    const serviceEndDate = agreementRow?.toDate || agreementDate;
    const paymentOption = "with_gst_invoice_acc";
    const agreementPaymentTerms = buildAgreementPaymentTermsForOrders(agreementRow, agreementDate);
    const pricingServices = [];

    const erpFeatures = Array.isArray(agreementRow?.erpfeature)
      ? agreementRow.erpfeature.map((x) => String(x)).filter(Boolean)
      : [];
    const erpBase = round2(erpAmountBeforeGst(agreementRow, studentStrengthOverride));
    if (erpBase > 0) {
      pricingServices.push({
        service_id: erpPlan.service,
        service_name: "ERP",
        plan_id: erpPlan.id,
        plan_name: "ERP",
        erpservice: Number(agreementRow?.id) || null,
        item_count: 1,
        service_amount: erpBase,
        service_gst_percentage: toNum(agreementRow?.gst),
        payment_option: paymentOption,
        service_duration: 1,
        order_date: agreementDate,
        service_end_date: serviceEndDate,
        notes: "Auto ERP order from agreement",
        payment_terms: agreementPaymentTerms,
      });
    }

    const perId = toNum(agreementRow?.perId);
    const fromAgreementStudents = toNum(agreementRow?.numberOfStudents);
    let studentStrength =
      studentStrengthOverride != null && Number.isFinite(Number(studentStrengthOverride))
        ? toNum(studentStrengthOverride)
        : fromAgreementStudents;
    if (studentStrength <= 0 && fromAgreementStudents > 0) {
      studentStrength = fromAgreementStudents;
    }
    const idCardBase = round2(idCardAmountTotal(agreementRow, studentStrength));
    if (idCardBase > 0 && perId > 0 && studentStrength > 0) {
      pricingServices.push({
        service_id: idCardPlan.service,
        service_name: "ID Cards",
        plan_id: idCardPlan.id,
        plan_name: "ID Cards",
        item_count: studentStrength,
        service_amount: perId,
        service_gst_percentage: toNum(agreementRow?.gst) || 18,
        payment_option: paymentOption,
        service_duration: 1,
        order_date: agreementDate,
        service_end_date: serviceEndDate,
        notes: "Auto ID card order from agreement",
        payment_terms: agreementPaymentTerms,
      });
    }

    Object.entries(digitalRaw).forEach(([featureId, meta]) => {
      const digitalFeatureId = Number(featureId);
      if (!Number.isInteger(digitalFeatureId)) return;
      let monthlyAmount = 0;
      let fromDate = agreementDate;
      let toDate = serviceEndDate;
      let itemCount = 1;
      if (Array.isArray(meta)) {
        // Legacy format: [amount, renewal_price]. Treat first value as monthly for backward compatibility.
        monthlyAmount = toNum(meta[0]);
      } else if (meta && typeof meta === "object") {
        monthlyAmount = toNum(meta.price);
        fromDate = meta.billing_from || agreementDate;
        toDate = meta.billing_to || serviceEndDate;
      }
      monthlyAmount = round2(monthlyAmount);
      if (monthlyAmount <= 0) return;
      const feature = digitalById.get(String(featureId));
      const calculationType = (meta && typeof meta === "object" && meta.calculation_type) || feature?.calculation_type || "monthwise";
      if (calculationType === "yearwise") {
        itemCount = getInclusiveYears(fromDate, toDate);
      } else if (calculationType === "studentcount") {
        itemCount = studentStrength;
      } else {
        itemCount = getInclusiveMonths(fromDate, toDate);
      }
      if (!itemCount || itemCount <= 0) return;
      if (!digitalPlan) return;
      const featureLabel = feature?.name || String(featureId);
      pricingServices.push({
        service_id: digitalPlan.service,
        service_name: featureLabel,
        plan_id: digitalPlan.id,
        plan_name: featureLabel,
        item_count: itemCount,
        digitalservice: digitalFeatureId,
        digitalfeature: digitalFeatureId,
        service_amount: monthlyAmount,
        service_gst_percentage: 0,
        payment_option: paymentOption,
        service_duration: 1,
        order_date: fromDate,
        service_end_date: toDate,
        notes: "Auto digital order from agreement",
        payment_terms: [],
      });
    });

    if (!pricingServices.length) {
      throw new Error("No ERP amount or digital feature amount found in agreement.");
    }
    return pricingServices;
  };

  React.useEffect(() => {
    loadRows(filters);
  }, [page, pageSize]);

  const createOrdersFromAgreement = async (agreementRow, configId, studentStrengthOverride = null) => {
    // Intentionally no order creation here.
    // Billing orders/services must be created only when the user submits on /billing/company-setup.
    return { agreementRow, configId, studentStrengthOverride };
  };

  const handleCreateOrder = async (row) => {
    if (!row?.company_created || !row?.company) {
      Swal.fire("Company required", "Create company before creating order", "warning");
      return;
    }
    try {
      const res = await getRequest(GET_URL.company_billing_config.api, { company: row.company, limit: 1, pageno: 1 }, props);
      const cfg = res?.data?.data?.[0];
      if (!cfg?.id) {
        // Fetch the company record to get the assigned coordinator_id
        let autoCoordinator = "";
        try {
          const companyRes = await getRequest(`${GET_URL.company.api}${row.company}/`, {}, props);
          const companyData = companyRes?.data?.data || companyRes?.data;
          if (companyData?.coordinator_id) {
            autoCoordinator = companyData.coordinator_id;
          }
        } catch (_) {
          // silently ignore; user can select manually
        }
        setBillingAgreementRow(row);
        setBillingForm({
          year: "",
          student_strength: row?.numberOfStudents || "",
          coordinator: autoCoordinator,
        });
        setBillingDialogOpen(true);
        return;
      }
      const rawStrength =
        cfg.student_strength != null && cfg.student_strength !== "" ? Number(cfg.student_strength) : null;
      const strengthOverride =
        rawStrength != null && Number.isFinite(rawStrength) ? rawStrength : null;
      // Do not create order here; just take user to billing setup page.
      await createOrdersFromAgreement(row, cfg.id, strengthOverride);
      navigate(`/billing/company-setup?config_id=${cfg.id}`);
    } catch (e) {
      Swal.fire("Error", e?.message || "Unable to load company billing config", "error");
    }
  };

  const handleCreateBillingConfigInDialog = async () => {
    if (!billingAgreementRow?.company) return;
    if (!billingForm.year || !billingForm.student_strength || !billingForm.coordinator) {
      Swal.fire("Missing data", "Please fill year, student strength and coordinator", "warning");
      return;
    }
    const strength = Number(billingForm.student_strength);
    if (!Number.isInteger(strength) || strength <= 0 || strength > 9999) {
      Swal.fire("Invalid student strength", "Enter a whole number between 1 and 9999", "warning");
      return;
    }
    setBillingSubmitting(true);
    try {
      const payload = [{
        company: billingAgreementRow.company,
        year: Number(billingForm.year),
        student_strength: strength,
        coordinator: Number(billingForm.coordinator),
      }];
      const response = await postRequest(POST_URL.company_billing_config.api, payload, props);
      if (response?.status !== 200) {
        throw new Error("Unable to create billing config");
      }
      const cfgRes = await getRequest(
        GET_URL.company_billing_config.api,
        { company: billingAgreementRow.company, year: billingForm.year, limit: 1, pageno: 1 },
        props
      );
      const cfg = cfgRes?.data?.data?.[0];
      if (!cfg?.id) {
        throw new Error("Billing config created but not found");
      }
      setBillingDialogOpen(false);
      // Do not create order here; just take user to billing setup page.
      navigate(`/billing/company-setup?config_id=${cfg.id}`);
    }  catch (e) {
      Swal.fire("Error", e?.message || "Unable to create billing config", "error");
    } finally {
      setBillingSubmitting(false);
    }
  };

  const columns = [
    { field: "orderformnumber", headerName: "Order #", width: 160 },
    { field: "schoolName", headerName: "Institute Name", width: 240 },
    { field: "salesPersonName", headerName: "Salesperson", width: 180 },
    { field: "created_by_name", headerName: "Created By", width: 160, valueGetter: (p) => p.row?.created_by_name || "-" },
    { field: "modified_by_name", headerName: "Modified By", width: 160, valueGetter: (p) => p.row?.modified_by_name || "-" },
    { field: "agreementdate", headerName: "Date", width: 150 },

    {
      field: "status",
      headerName: "Status",
      width: 170,
      renderCell: (p) => (
        <Chip label={STATUS_LABELS[p.value] || p.value} color={getStatusColor(p.value)} size="small" />
      ),
    },
    {
      field: "company_created",
      headerName: "Company",
      width: 160,
      valueGetter: (p) => (p.row?.company_created ? "Created" : "Not Created"),
    },
    {
      field: "order_created",
      headerName: "Order",
      width: 140,
      valueGetter: (p) => (p.row?.order_created ? "Created" : "Not Created"),
    },

    {
      field: "actions",
      type: "actions",
      getActions: (p) => {
        const isDraft = p.row?.status === "DRAFT";
        const isSigned = p.row?.status === "SIGNED";
        const isSignedCreated = p.row?.status === "SIGNED_AND_CREATED";
        const isCreated = p.row?.status === "CREATED";
        const isCompanyCreated = Boolean(p.row?.company_created);
        const isOrderCreated = Boolean(p.row?.order_created);
        return [
          <GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => handleEdit(p.row)} showInMenu />,
          <GridActionsCellItem icon={<Visibility />} label="Preview" onClick={() => handlePreview(p.row)} showInMenu />,
          <GridActionsCellItem icon={<Print />} label="Print" onClick={() => handlePrint(p.row)} showInMenu />,
          ...(isDraft
            ? [
                <GridActionsCellItem
                  icon={<TaskAlt />}
                  label="Make signed"
                  onClick={() => handleMakeSigned(p.row)}
                  showInMenu
                />,
              ]
            : []),
          ...(isSigned ? [
            <GridActionsCellItem
              icon={<Business />}
              label="Create Company"
              onClick={() => handleCreateCompany(p.row)}
              disabled={isCompanyCreated}
              showInMenu
            />
          ] : []),
          ...(isSignedCreated || isCreated ? [
            <GridActionsCellItem
              icon={<ShoppingCart />}
              label="Create Order"
              onClick={() => handleCreateOrder(p.row)}
              disabled={isOrderCreated}
              showInMenu
            />
          ] : []),
        ];
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>

      {/* HEADER */}
      <Grid container alignItems="center">
        <Grid item xs={6}>
          <Typography variant="h5" fontWeight={600}>Agreements</Typography>
          <Typography variant="body2" color="text.secondary">Manage agreements</Typography>
        </Grid>

        <Grid item xs={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/agreement/add"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{ borderRadius: 2 }}
          >
            Add Agreement
          </Button>
        </Grid>
      </Grid>

      {/* FILTER CARD */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <TextField select fullWidth label="Salesperson"
              value={filters.sales_person}
              onChange={(e) => setFilters({ ...filters, sales_person: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {salesPeopleOptions.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={3}>
            <TextField fullWidth type="date" label="From" InputLabelProps={{ shrink: true }}
              value={filters.agreementDateFrom}
              onChange={(e) => setFilters({ ...filters, agreementDateFrom: e.target.value })}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField fullWidth type="date" label="To" InputLabelProps={{ shrink: true }}
              value={filters.agreementDateTo}
              onChange={(e) => setFilters({ ...filters, agreementDateTo: e.target.value })}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField fullWidth label="Search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABLE */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={(nextPage) => setPage(nextPage)}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(
              AGREEMENT_PAGE_SIZE_OPTIONS.includes(nextPageSize)
                ? nextPageSize
                : DEFAULT_AGREEMENT_PAGE_SIZE
            );
            setPage(0);
          }}
          pageSizeOptions={AGREEMENT_PAGE_SIZE_OPTIONS}
          rowCount={rowCount}
          autoHeight
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": { background: "#f5f7fb" }
          }}
        />
      </Paper>

      <Dialog open={billingDialogOpen} onClose={() => setBillingDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Company Billing Config</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                value={billingAgreementRow?.companyName || "Selected from agreement"}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={billingForm.year}
                onChange={(e) => setBillingForm((prev) => ({ ...prev, year: e.target.value }))}
              >
                {billingYears.map((y) => (
                  <MenuItem key={y.id} value={y.id}>
                    {y.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Student Strength"
                value={billingForm.student_strength}
                onChange={(e) => setBillingForm((prev) => ({ ...prev, student_strength: e.target.value }))}
                inputProps={{ min: 1, max: 9999, step: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Coordinator"
                value={billingForm.coordinator}
                onChange={(e) => setBillingForm((prev) => ({ ...prev, coordinator: e.target.value }))}
              >
                {billingUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username || u.email || u.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleCreateBillingConfigInDialog} disabled={billingSubmitting} variant="contained">
            Create & Continue
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default AgreementListPage;
